import React, { useEffect, useMemo, useState } from 'react';
import { getKaraokeQueueItemStage } from '../../utils/karaokeRuntimeState';

const TERMINAL_STATUSES = new Set(['rejected', 'played', 'skipped', 'expired']);
const PLAYED_STATUS = 'played';

const titleCase = (value = '') =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const formatTimestamp = (value, timeZone = null) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  try {
    return date.toLocaleString([], timeZone ? { timeZone, timeZoneName: 'short' } : undefined);
  } catch (_error) {
    return date.toLocaleString();
  }
};

const buildParticipantMap = (items = []) => new Map(items.map((item) => [Number(item.id), item]));

const getStatus = (item) => String(item?.status || '').toLowerCase();

const normalizeVolume = (value, fallback = 100) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, Math.round(parsed)));
};

const compareByPosition = (a, b) => {
  const positionDelta = Number(a.positionValue || 0) - Number(b.positionValue || 0);
  if (positionDelta !== 0) return positionDelta;
  return Number(a.id || 0) - Number(b.id || 0);
};

const compareByUpdatedDesc = (a, b) => {
  const aUpdatedAt = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
  const bUpdatedAt = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
  if (aUpdatedAt !== bUpdatedAt) return bUpdatedAt - aUpdatedAt;
  return Number(b.id || 0) - Number(a.id || 0);
};

function RuntimeChip({ children, tone = 'default' }) {
  const toneClass = {
    success: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/20',
    warning: 'bg-amber-500/15 text-amber-100 border-amber-400/20',
    danger: 'bg-rose-500/15 text-rose-100 border-rose-400/20',
    info: 'bg-sky-500/15 text-sky-100 border-sky-400/20',
    default: 'bg-white/5 text-slate-200 border-white/10',
  }[tone] || 'bg-white/5 text-slate-200 border-white/10';

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${toneClass}`}>
      {children}
    </span>
  );
}

function ActionButton({ label, onClick, disabled = false, tone = 'default', type = 'button' }) {
  const toneClass = {
    primary: 'bg-emerald-600/90 hover:bg-emerald-600 text-white',
    warning: 'bg-amber-600/80 hover:bg-amber-600 text-white',
    danger: 'bg-rose-600/80 hover:bg-rose-600 text-white',
    default: 'bg-white/10 hover:bg-white/20 text-slate-100',
  }[tone] || 'bg-white/10 hover:bg-white/20 text-slate-100';

  return (
    <button
      type={type}
      disabled={disabled}
      className={`rounded-xl px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${toneClass}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default function KaraokeRuntimePanel({
  runtime,
  loading = false,
  acting = false,
  onRefresh,
  onCopyLink,
  onReset,
  onAction,
  onSearchCatalog,
  onForceQueueItem,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [forceQueueMessage, setForceQueueMessage] = useState('');
  const [performerVolume, setPerformerVolume] = useState(100);
  const [backgroundVolume, setBackgroundVolume] = useState(100);
  const [volumeMessage, setVolumeMessage] = useState('');
  const snapshot = runtime?.snapshot || null;
  const session = snapshot?.session || null;
  const settings = session?.state?.settings || {};
  const lifecycle = session?.state?.lifecycle || {};
  const queueItems = Array.isArray(snapshot?.queueItems) ? snapshot.queueItems : [];
  const participants = Array.isArray(snapshot?.participants) ? snapshot.participants : [];
  const participantMap = useMemo(() => buildParticipantMap(participants), [participants]);
  const runtimeState = session?.state?.runtime || {};
  const volumeSettings = settings.karaokeVolumes || settings.karaokeVolumeControls || {};
  const activeQueueItemId = Number(runtimeState.activeQueueItemId) || null;
  const promotionScreenDeviceIds = Array.isArray(settings.promotionScreenDeviceIds)
    ? settings.promotionScreenDeviceIds
    : Array.isArray(settings.audienceDisplayDeviceIds)
      ? settings.audienceDisplayDeviceIds
      : [];
  const performerScreenDeviceId = Number(settings.performerScreenDeviceId) || null;
  const lastCompletedQueueItemId = Number(runtimeState.lastCompletedQueueItemId) || null;
  const playedQueue = useMemo(
    () => queueItems
      .filter((item) => getStatus(item) === PLAYED_STATUS)
      .sort(compareByUpdatedDesc),
    [queueItems]
  );
  const liveQueue = useMemo(() => {
    const active = [];
    const upcoming = [];
    const terminal = [];

    queueItems.forEach((item) => {
      const itemStatus = getStatus(item);
      if (itemStatus === PLAYED_STATUS) return;

      if (Number(item.id) === activeQueueItemId && !TERMINAL_STATUSES.has(itemStatus)) {
        active.push(item);
        return;
      }

      if (TERMINAL_STATUSES.has(itemStatus)) {
        terminal.push(item);
        return;
      }

      upcoming.push(item);
    });

    return [
      ...active.sort(compareByPosition),
      ...upcoming.sort(compareByPosition),
      ...terminal.sort(compareByPosition),
    ];
  }, [activeQueueItemId, queueItems]);
  const activeItem = queueItems.find((item) => Number(item.id) === activeQueueItemId) || null;
  const nextItem = liveQueue.find((item) =>
    Number(item.id) !== activeQueueItemId
      && !TERMINAL_STATUSES.has(getStatus(item))
  ) || null;
  const lastCompletedItem = queueItems.find((item) => Number(item.id) === lastCompletedQueueItemId)
    || playedQueue[0]
    || null;

  useEffect(() => {
    setPerformerVolume(normalizeVolume(volumeSettings.performer ?? volumeSettings.performerVolume, 100));
    setBackgroundVolume(normalizeVolume(volumeSettings.background ?? volumeSettings.backgroundVolume, 100));
  }, [volumeSettings.background, volumeSettings.backgroundVolume, volumeSettings.performer, volumeSettings.performerVolume]);

  const handleApplyVolumes = async () => {
    if (typeof onAction !== 'function') return;
    try {
      setVolumeMessage('');
      await onAction('set_volumes', null, {
        performerVolume,
        backgroundVolume,
      });
      setVolumeMessage('Karaoke volume limits applied.');
    } catch (error) {
      setVolumeMessage(error.message || 'Failed to apply karaoke volumes.');
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query || typeof onSearchCatalog !== 'function') return;

    try {
      setSearching(true);
      setForceQueueMessage('');
      const result = await onSearchCatalog(query);
      setSearchResults(Array.isArray(result?.items) ? result.items : []);
      if (!result?.items?.length) {
        setForceQueueMessage('No YouTube results found.');
      }
    } catch (error) {
      setSearchResults([]);
      setForceQueueMessage(error.message || 'YouTube search failed.');
    } finally {
      setSearching(false);
    }
  };

  const handleForceQueue = async (item) => {
    if (!item || typeof onForceQueueItem !== 'function') return;

    try {
      setForceQueueMessage('');
      await onForceQueueItem(item);
      setForceQueueMessage(`Forced "${item.title || 'selected song'}" to the front of the queue.`);
    } catch (error) {
      setForceQueueMessage(error.message || 'Failed to force song into the queue.');
    }
  };

  const renderSearchResult = (item) => (
    <div key={item.videoId} className="grid gap-3 rounded-2xl bg-white/5 p-3 ring-1 ring-white/10 sm:grid-cols-[8rem,1fr]">
      <div className="aspect-video overflow-hidden rounded-xl bg-black/30">
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-white break-words">{item.title}</div>
        <div className="mt-1 text-xs text-slate-400">{item.channelTitle || 'YouTube'}</div>
        {item.description ? (
          <div className="mt-2 line-clamp-2 text-xs text-slate-500">{item.description}</div>
        ) : null}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <ActionButton
            label="Force To Front"
            onClick={() => handleForceQueue(item)}
            disabled={acting}
            tone="primary"
          />
          {item.watchUrl ? (
            <a
              href={item.watchUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-slate-100 hover:bg-white/20"
            >
              Preview
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderQueueItem = (item, index, sectionLabel) => {
    const participant = participantMap.get(Number(item.submittedByParticipantId)) || null;
    const isActive = Number(item.id) === activeQueueItemId;
    const playbackStage = getKaraokeQueueItemStage(runtimeState, item.id);
    const isTerminal = TERMINAL_STATUSES.has(getStatus(item));
    const youtube = item.payload?.youtube || null;
    const requesterLabel = item.payload?.forcedByHost
      ? 'Host override'
      : participant?.nickname || 'Unknown guest';

    return (
      <div
        key={item.id}
        className={`rounded-2xl p-4 ring-1 grid gap-3 ${
          isActive
            ? 'bg-emerald-500/10 ring-emerald-400/40'
            : 'bg-white/5 ring-white/10'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{sectionLabel} #{index + 1}</span>
              {playbackStage === 'performing' ? <RuntimeChip tone="success">Performing</RuntimeChip> : null}
              {playbackStage === 'pending' ? <RuntimeChip tone="info">Start pending</RuntimeChip> : null}
              {playbackStage === 'prepared' ? <RuntimeChip tone="info">Prepared</RuntimeChip> : null}
              <RuntimeChip tone={isTerminal ? 'warning' : 'default'}>{titleCase(item.status || 'queued')}</RuntimeChip>
            </div>
            <div className="mt-2 text-base font-semibold text-white break-words">{item.label}</div>
            <div className="mt-1 text-sm text-slate-300">
              {requesterLabel} • {titleCase(item.sourceType || 'request')}
              {item.votesScore ? ` • Score ${item.votesScore}` : ''}
            </div>
            {youtube?.watchUrl ? (
              <a
                href={youtube.watchUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex text-xs text-sky-200 underline"
              >
                Open YouTube reference
              </a>
            ) : null}
            {item.payload?.note ? (
              <div className="mt-2 text-sm text-slate-400">{item.payload.note}</div>
            ) : null}
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>Added {formatTimestamp(item.createdAt)}</div>
            <div>Updated {formatTimestamp(item.updatedAt)}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {!isTerminal ? (
            <>
              <ActionButton label="Start" onClick={() => onAction('start_item', item.id)} disabled={acting || !performerScreenDeviceId} tone="primary" />
              <ActionButton label="Played" onClick={() => onAction('mark_played', item.id)} disabled={acting} tone="primary" />
              <ActionButton label="Cancel" onClick={() => onAction('cancel_item', item.id)} disabled={acting} tone="danger" />
            </>
          ) : (
            <ActionButton label="Return To Queue" onClick={() => onAction('requeue_item', item.id)} disabled={acting} />
          )}
          <ActionButton label="Up" onClick={() => onAction('move_up', item.id)} disabled={acting || isTerminal} />
          <ActionButton label="Down" onClick={() => onAction('move_down', item.id)} disabled={acting || isTerminal} />
          <ActionButton label="Top" onClick={() => onAction('move_top', item.id)} disabled={acting || isTerminal} />
          <ActionButton label="Bottom" onClick={() => onAction('move_bottom', item.id)} disabled={acting || isTerminal} />
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="text-sm text-slate-300">Loading karaoke runtime…</div>;
  }

  if (!runtime?.hasLiveSession || !session) {
    return (
      <div className="grid gap-3">
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 text-sm text-slate-200">
          Launch the karaoke session first. Once it is live, this panel will show the guest queue and host controls.
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 md:grid-cols-[1.2fr,0.8fr]">
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-amber-200">Live Session</div>
              <div className="text-lg font-semibold text-white">
                {settings.publicTitle || runtime?.eventInstance?.name || 'Karaoke Night'}
              </div>
              {settings.publicTitle ? (
                <div className="text-xs text-indigo-200">Powered by {settings.engineLabel || 'Karaoke'}</div>
              ) : null}
              <div className="text-xs text-slate-400">Session key: {session.publicKey}</div>
              <div className="text-xs text-slate-400">
                {lifecycle.resolvedEndAt
                  ? `Ends ${formatTimestamp(lifecycle.resolvedEndAt, lifecycle.sourceTimeZone || 'UTC')}`
                  : 'Runs until stopped'}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <RuntimeChip tone={session.status === 'live' ? 'success' : 'warning'}>
                {titleCase(session.status || 'unknown')}
              </RuntimeChip>
              <RuntimeChip tone="info">{titleCase(session.currentPhase || 'queue_open')}</RuntimeChip>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">Guests</div>
              <div className="mt-1 text-xl font-semibold text-white">{snapshot?.counts?.activeParticipants || 0}</div>
            </div>
            <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">Queue</div>
              <div className="mt-1 text-xl font-semibold text-white">{liveQueue.length}</div>
            </div>
            <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">Runtime</div>
              <div className="mt-1 text-sm font-semibold text-white">{titleCase(runtimeState.interstitialMode || 'ambient_video')}</div>
            </div>
          </div>

          <div className="text-xs text-slate-400">
            Performer screen: {performerScreenDeviceId ? `device #${performerScreenDeviceId}` : 'not configured in Karaoke setup'}
            {promotionScreenDeviceIds.length ? ` • ${promotionScreenDeviceIds.length} promotion screen${promotionScreenDeviceIds.length === 1 ? '' : 's'}` : ''}
          </div>

          <div className="flex flex-wrap gap-2">
            <ActionButton label="Refresh" onClick={onRefresh} disabled={acting} />
            <ActionButton label="Copy Guest Link" onClick={onCopyLink} disabled={acting} />
            <ActionButton label="Reset Session" onClick={onReset} disabled={acting} tone="danger" />
            <ActionButton label="Queue Open" onClick={() => onAction('queue_open')} disabled={acting} />
            <ActionButton label="Silence" onClick={() => onAction('silence')} disabled={acting} tone="warning" />
            <ActionButton label="Ambient" onClick={() => onAction('ambient')} disabled={acting} />
            {session.status === 'paused' ? (
              <ActionButton label="Resume Session" onClick={() => onAction('resume')} disabled={acting} tone="primary" />
            ) : (
              <ActionButton label="Pause Session" onClick={() => onAction('pause')} disabled={acting} tone="warning" />
            )}
          </div>

          <div className="rounded-2xl bg-black/20 ring-1 ring-white/10 p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-emerald-200">Karaoke Volume Limits</div>
                <div className="text-sm text-slate-400">
                  Fade transitions stay active. These values set the target volume for performer videos and fallback background playback.
                </div>
              </div>
              <ActionButton
                label="Apply Volumes"
                onClick={handleApplyVolumes}
                disabled={acting}
                tone="primary"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm text-slate-300">
                <span className="flex items-center justify-between gap-3">
                  <span>Performer backing video max</span>
                  <strong className="text-white">{performerVolume}%</strong>
                </span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={performerVolume}
                  onChange={(event) => setPerformerVolume(normalizeVolume(event.target.value, performerVolume))}
                  className="w-full accent-emerald-400"
                />
              </label>
              <label className="grid gap-2 text-sm text-slate-300">
                <span className="flex items-center justify-between gap-3">
                  <span>Background / fallback max</span>
                  <strong className="text-white">{backgroundVolume}%</strong>
                </span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={backgroundVolume}
                  onChange={(event) => setBackgroundVolume(normalizeVolume(event.target.value, backgroundVolume))}
                  className="w-full accent-sky-400"
                />
              </label>
            </div>
            {volumeMessage ? (
              <div className="mt-3 rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-300 ring-1 ring-white/10">
                {volumeMessage}
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-sky-200">Performance Flow</div>
            <div className="mt-2 text-sm text-slate-300">Start the next singer when they are ready. When the track finishes, the song is marked played automatically and the performer screen returns to the promotion view.</div>
          </div>
          <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">Active</div>
            <div className="mt-1 text-sm font-semibold text-white">
              {activeItem?.label || 'None'}
            </div>
          </div>
          <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">Next Up</div>
            <div className="mt-1 text-sm font-semibold text-white">
              {nextItem?.label || 'None'}
            </div>
          </div>
          <div className="rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-400">Last Played</div>
            <div className="mt-1 text-sm font-semibold text-white">
              {lastCompletedItem?.label || 'None'}
            </div>
          </div>
          <div className="text-xs text-slate-500">Last runtime update: {formatTimestamp(runtimeState.updatedAt)}</div>
        </div>
      </div>

      <div className="rounded-2xl bg-black/20 ring-1 ring-white/10 p-4">
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-emerald-200">Force Song Into Queue</div>
            <div className="text-sm text-slate-400">
              Search YouTube and add an operator-selected track to the front of the upcoming queue.
            </div>
          </div>
          <RuntimeChip tone="success">Host Override</RuntimeChip>
        </div>
        <form className="flex flex-col gap-2 sm:flex-row" onSubmit={handleSearch}>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search artist, song, or YouTube title"
            className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-emerald-300 focus:outline-none"
          />
          <ActionButton
            label={searching ? 'Searching...' : 'Search YouTube'}
            disabled={acting || searching || !searchQuery.trim()}
            tone="primary"
            type="submit"
          />
        </form>
        {forceQueueMessage ? (
          <div className="mt-3 rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-300 ring-1 ring-white/10">
            {forceQueueMessage}
          </div>
        ) : null}
        {searchResults.length ? (
          <div className="mt-4 grid max-h-[32rem] gap-3 overflow-y-auto pr-2">
            {searchResults.map(renderSearchResult)}
          </div>
        ) : null}
      </div>

      <div className="grid gap-4">
        <div className="rounded-2xl bg-black/20 ring-1 ring-white/10 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-sky-200">Now Playing / Next In Queue</div>
              <div className="text-sm text-slate-400">Updates as guests add songs and the host starts tracks.</div>
            </div>
            <RuntimeChip>{liveQueue.length} Queued</RuntimeChip>
          </div>
          {liveQueue.length ? (
            <div className="grid max-h-[80rem] gap-3 overflow-y-auto pr-2">
              {liveQueue.map((item, index) => renderQueueItem(item, index, 'Queue'))}
            </div>
          ) : (
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 text-sm text-slate-300">
              No karaoke requests yet.
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-black/20 ring-1 ring-white/10 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-amber-200">Played Tracks</div>
              <div className="text-sm text-slate-400">Visible only in this runtime view.</div>
            </div>
            <RuntimeChip tone="info">{playedQueue.length} Played</RuntimeChip>
          </div>
          {playedQueue.length ? (
            <div className="grid max-h-[40rem] gap-3 overflow-y-auto pr-2">
              {playedQueue.map((item, index) => renderQueueItem(item, index, 'Played'))}
            </div>
          ) : (
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 text-sm text-slate-300">
              No songs have been marked played yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
